<script lang="ts" setup>
import type { ToMetaTagsType } from "vue-datocms";
import type { HeaderSectionRecord, HourRecord, StoreRecord } from "~~/server/graphql/datocms/generated";
import { CustomMarker, GoogleMap } from "vue3-google-map";
import { toHead } from "vue-datocms";
import { isBellaSiciliaByDomain } from "~/utils/stores";

const { locale, t } = useI18n();
const { params } = useRoute();
const slug = params.slug || "home";

const { data } = await useFetch<StoreRecord>("/api/cms/stores/detail", {
  query: { slug: slug, locale: locale },
});

if (!data.value) {
  throw create404Error();
}

useDatoSlugsToI18n(data.value._allSlugLocales!);

useHead(() => {
  if (!data.value) {
    return {};
  }

  return toHead(data.value._seoMetaTags as ToMetaTagsType);
});

// local business (https://schema.org/LocalBusiness)
// opening hours (https://schema.org/OpeningHoursSpecification)
useSchemaOrg([
  defineLocalBusiness({
    name: data.value.name,
    address: {
      addressCountry: data.value.addressFields.countryCode,
      addressLocality: data.value.addressFields.city,
      postalCode: data.value.addressFields.postalCode,
      streetAddress: data.value.addressFields.street,
    },
    email: data.value.optEmailAddress,
    telephone: data.value.optPhoneNumber,
    faxNumber: data.value.optFaxNumber,
    openingHoursSpecification: data.value.openingHours.map((oh: HourRecord) => ({
      dayOfWeek: `https://schema.org/${getWeekdayLocale("en-gb", "long")[Number.parseInt(oh.dayOfWeek) - 1]}`,
      opens: oh.startHour,
      closes: oh.endHour,
    })),
  }),
]);

const headerSection = computed(() => {
  return data.value?.sections.find(s => s.__typename === "HeaderSectionRecord") as HeaderSectionRecord;
});

const footerSections = computed(() => {
  return data.value?.sections.filter(s => s.__typename !== "HeaderSectionRecord");
});

const { GOOGLE_MAPS_API_KEY_SPUNTINI, GOOGLE_MAPS_API_KEY_BELLASICILIA } = useRuntimeConfig().public;

const googleMapsApiKey = computed(() => isBellaSiciliaByDomain() ? GOOGLE_MAPS_API_KEY_BELLASICILIA : GOOGLE_MAPS_API_KEY_SPUNTINI);
const googleMapsRef = ref(null as any);
const showGoogleMapsMarker = computed(() => googleMapsRef.value?.ready && data.value?.location);

// remove default POIs
const googleMapsStyle = [
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [
      { visibility: "off" },
    ],
  },
];
</script>

<template>
  <div class="mt-3xl">
    <Breadcrumbs class="mb-sm" :page="data" page-type="stores" />

    <article v-if="data">
      <div v-if="headerSection" class="header-section">
        <SectionsHeader :data="headerSection" :page-title="data.name" :current-page-id="data.id" />
      </div>

      <div class="gap-xl mb-3xl grid grid-cols-1 lg:grid-cols-2">
        <div>
          <h4 class="mb-xs">
            {{ t('store.title.contact-info') }}
          </h4>

          <div class="mb-xl">
            <div class="mb-md" v-html="data.address" />
            <div v-if="data.optPhoneNumber">
              {{ data.optPhoneNumber }}
            </div>
            <div v-if="data.optFaxNumber" class="mb-md">
              {{ data.optFaxNumber }}
            </div>
            <a v-if="data.optEmailAddress" :href="`mailto:${data.optEmailAddress}`" class="text-primary underline">
              {{ data.optEmailAddress }}
            </a>
          </div>

          <h4 class="mb-xs">
            {{ t('store.title.opening-hours') }}
          </h4>

          <div class="mb-xl w-full md:w-4/5">
            <StoreOpeningHours :opening-hours="data.openingHours" />
          </div>
        </div>

        <div v-if="data.location">
          <h4 class="mb-md">
            {{ t('store.title.location') }}
          </h4>

          <div class="mb-md">
            <GoogleMap
              ref="googleMapsRef" :api-key="googleMapsApiKey" :center="{ lat: data.location.latitude, lng: data.location.longitude }"
              :street-view-control="false" :styles="googleMapsStyle" :zoom="15" style="width: 100%; height: 500px"
            >
              <CustomMarker
                v-show="showGoogleMapsMarker"
                :options="{ position: { lat: data.location.latitude, lng: data.location.longitude }, anchorPoint: 'BOTTOM_CENTER' }"
              >
                <Vector class="vector--marker size-8 rotate-10" />
              </CustomMarker>
            </GoogleMap>
          </div>

          <UButton
            variant="solid" color="primary" size="md"
            :to="`https://www.google.com/maps/search/?api=1&query=${data.location.latitude},${data.location.longitude}`" target="_blank"
          >
            {{ t('store.label.route') }}
          </UButton>
        </div>
      </div>

      <SectionsRenderer :sections="footerSections" :page-title="data.name" :current-page-id="data.id" />
    </article>
  </div>
</template>

<style>
.vector--marker {
  --esc-vector-fill-color: #c81414;
  --esc-vector-mix-blend-mode: normal;
  --esc-vector-opacity: 1;
}
</style>
